// packages/web-ui/src/api/clients/capabilities.ts
//
// Story W3-S6 — typed HTTP client for the four capability-surface endpoints
// the SPA needs to wire up the live `<CapabilityPillRow>` and the
// `<HealthStatusBanner>` polling loop:
//
//   - `GET  /api/v1/health`                 → `HealthResponse` (the polling source)
//   - `GET  /api/v1/system/capabilities`    → `Capabilities`   (debug-power-tool fallback)
//   - `POST /api/v1/system/workspace-changed` → `WorkspaceChangedResponse`
//   - `POST /api/v1/system/open-in-syside`  → `OpenInSysideResponse`
//
// Mirrors the W4-S5 `kb.ts` / W6-S2 `settings.ts` client patterns:
// `Result<T, ApiError>` envelope per CLAUDE.md "Coding Standards"; uses the
// shared `apiCall` helper for bearer-token + X-Request-Id wiring; types come
// from `@tessa-lean/base/api-schemas`.
//
// Story scope (per orchestrator constraint): client wiring only — the
// polling state slice (`state/capabilities.ts`), the React hook
// (`hooks/useCapabilities.ts`), and the pill components live in their own
// files. AppShell wiring (banner / status-bar) is deferred to a follow-up
// integration tick.

import type { Result } from '@tessa-lean/base';
import type {
  Capabilities,
  HealthResponse,
  OpenInSysideRequest,
  OpenInSysideResponse,
  WorkspaceChangedRequest,
  WorkspaceChangedResponse,
} from '@tessa-lean/base/api-schemas/index.js';
import { apiCall, type ApiContext, type ApiError } from '../client.js';

// ─── defaultFetch seam (BLOCKER-2 final fix-pass) ────────────────────────
//
// The capabilities slice (`state/capabilities.ts`) auto-fires `pollOnce()`
// on `startPolling()`, which is invoked by `useCapabilities()` on mount.
// In jsdom test environments where consumer components transitively mount
// `<FilesMode>` / `<FileEditor>` / etc., that eager poll runs against
// whatever `globalThis.fetch` is at the moment the slice's default
// `ApiContext` is constructed — which captures the production `fetch`
// before any test stub is installed in `beforeEach`. The unhandled
// rejection then cascades into 9+ component test failures (per the
// migration wiring audit §11.1).
//
// `_setDefaultFetchForTests(fn)` lets a test harness install a stub
// fetch that the default `ApiContext` will resolve at call time. In
// production, `defaultFetch` resolves to `globalThis.fetch` lazily on
// each call — preserving the previous behaviour.

let defaultFetch: typeof fetch | null = null;

/** Returns the active default fetch — the test override if set, else the global. */
function resolveDefaultFetch(): typeof fetch {
  return defaultFetch ?? globalThis.fetch;
}

/**
 * Test-only seam — installs a stub fetch that every API context built
 * without an explicit `fetchImpl` will use. Pass `null` to clear.
 *
 * Usage in tests (Vitest):
 *   beforeEach(() => {
 *     _setDefaultFetchForTests(vi.fn(async () => new Response('{}')));
 *   });
 *   afterEach(() => {
 *     _setDefaultFetchForTests(null);
 *   });
 */
export function _setDefaultFetchForTests(fn: typeof fetch | null): void {
  defaultFetch = fn;
}

/** Returns a copy of `ctx` with `fetchImpl` populated from the seam if absent. */
function withDefaultFetch(ctx: ApiContext): ApiContext {
  if (ctx.fetchImpl !== undefined) return ctx;
  return { ...ctx, fetchImpl: resolveDefaultFetch() };
}

/**
 * `GET /api/v1/health` → `HealthResponse`. The primary polling source for
 * the SPA's capability surface. The pill row consumes `response.capabilities`
 * directly.
 */
export function getHealth(
  ctx: ApiContext,
): Promise<Result<HealthResponse, ApiError>> {
  return apiCall<undefined, HealthResponse>(withDefaultFetch(ctx), 'GET', '/api/v1/health');
}

/**
 * `GET /api/v1/system/capabilities` → `Capabilities`. Returns the full
 * capability snapshot (including `hasSyside` / `hasMcpServers` which are
 * NOT projected through `/health`). Used by the W6-S2 settings panel's
 * "Force capability re-detection" power tool; the pill row prefers
 * {@link getHealth} for its 5 s polling.
 */
export function getCapabilities(
  ctx: ApiContext,
): Promise<Result<Capabilities, ApiError>> {
  return apiCall<undefined, Capabilities>(
    withDefaultFetch(ctx),
    'GET',
    '/api/v1/system/capabilities',
  );
}

/**
 * `POST /api/v1/system/workspace-changed` → `WorkspaceChangedResponse`.
 * Notifies the server that the workspace root has changed (or the SPA wants
 * to force a refresh). The server re-runs capability detection and returns
 * the new snapshot.
 *
 * Per W3-S6 AC #7, the v1 server does NOT support runtime workspace switch
 * (returns 501 `WORKSPACE_RUNTIME_SWITCH_UNSUPPORTED` when the requested
 * root differs from the booted root). Callers should surface that error
 * through the standard `ApiError` toast surface.
 */
export function notifyWorkspaceChanged(
  ctx: ApiContext,
  body: WorkspaceChangedRequest,
): Promise<Result<WorkspaceChangedResponse, ApiError>> {
  return apiCall<WorkspaceChangedRequest, WorkspaceChangedResponse>(
    withDefaultFetch(ctx),
    'POST',
    '/api/v1/system/workspace-changed',
    body,
  );
}

/**
 * `POST /api/v1/system/open-in-syside` → `OpenInSysideResponse`. Spawns the
 * configured `syside` IDE binary against the given workspace-relative path.
 *
 * Per W3-S6 Dev Notes "Why `caps.hasSyside` does NOT gate the open-in-syside
 * button" — the spawn attempts unconditionally; failure (e.g. ENOENT for a
 * missing binary) surfaces as `{ ok: false, error }` in the response body
 * (NOT as an `ApiError` envelope) so the toast surface can include the
 * `[Install syside]` link contract from FR-SYSIDE-4.
 */
export function openInSyside(
  ctx: ApiContext,
  input: OpenInSysideRequest,
): Promise<Result<OpenInSysideResponse, ApiError>> {
  return apiCall<OpenInSysideRequest, OpenInSysideResponse>(
    withDefaultFetch(ctx),
    'POST',
    '/api/v1/system/open-in-syside',
    input,
  );
}

/** Convenience barrel for tests + slice consumers. */
export const capabilitiesApi = {
  getHealth,
  getCapabilities,
  notifyWorkspaceChanged,
  openInSyside,
};

export type CapabilitiesApi = typeof capabilitiesApi;
